import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CROP_OUTPUT_SIZE,
  clampPan,
  coverScale,
  exportCroppedJpeg,
  type CropViewState,
} from '../lib/cropImage'
import { loadImage } from '../lib/molduraCanvas'

type Props = {
  /** Object URL ou data URL da imagem a recortar. */
  src: string
  title?: string
  onCancel: () => void
  onConfirm: (file: File) => void | Promise<void>
}

const PREVIEW = 280

export function ImageCropModal({ src, title = 'Ajustar foto', onCancel, onConfirm }: Props) {
  const imgRef = useRef<HTMLImageElement | null>(null)
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null)
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [zoom, setZoom] = useState(1)
  const [panX, setPanX] = useState(0)
  const [panY, setPanY] = useState(0)

  useEffect(() => {
    let cancelled = false
    setReady(false)
    setZoom(1)
    setPanX(0)
    setPanY(0)
    setError('')
    ;(async () => {
      try {
        const img = await loadImage(src)
        if (cancelled) return
        imgRef.current = img
        setReady(true)
      } catch {
        if (!cancelled) setError('Não foi possível carregar a imagem.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [src])

  const applyPan = useCallback((nx: number, ny: number, z: number) => {
    const img = imgRef.current
    if (!img) return
    const c = clampPan(img.naturalWidth, img.naturalHeight, PREVIEW, z, nx, ny)
    setPanX(c.panX)
    setPanY(c.panY)
  }, [])

  function onZoomChange(next: number) {
    const z = Math.min(3, Math.max(1, next))
    setZoom(z)
    applyPan(panX, panY, z)
  }

  function onPointerDown(e: React.PointerEvent) {
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = { x: e.clientX, y: e.clientY, panX, panY }
  }

  function onPointerMove(e: React.PointerEvent) {
    const d = dragRef.current
    if (!d) return
    applyPan(d.panX + (e.clientX - d.x), d.panY + (e.clientY - d.y), zoom)
  }

  function onPointerUp(e: React.PointerEvent) {
    dragRef.current = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
  }

  async function confirm() {
    const img = imgRef.current
    if (!img || busy) return
    setBusy(true)
    setError('')
    try {
      // Pan/zoom do preview → escala proporcional na saída 800
      const scaleFactor = CROP_OUTPUT_SIZE / PREVIEW
      const state: CropViewState = {
        zoom,
        panX: panX * scaleFactor,
        panY: panY * scaleFactor,
      }
      const blob = await exportCroppedJpeg(img, state, CROP_OUTPUT_SIZE)
      const file = new File([blob], 'perfil.jpg', { type: 'image/jpeg', lastModified: Date.now() })
      await onConfirm(file)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao recortar.')
      setBusy(false)
    }
  }

  const img = imgRef.current
  const base = img ? coverScale(img.naturalWidth, img.naturalHeight, PREVIEW) * zoom : 1
  const dw = img ? img.naturalWidth * base : 0
  const dh = img ? img.naturalHeight * base : 0

  return (
    <div className="bp-crop-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="bp-crop-modal">
        <h2 className="bp-crop-modal__title">{title}</h2>
        <p className="bp-muted" style={{ marginTop: 0 }}>
          Arraste para enquadrar e use o zoom para aproximar ou afastar.
        </p>

        <div
          className="bp-crop-stage"
          style={{ width: PREVIEW, height: PREVIEW }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {ready && img ? (
            <img
              src={src}
              alt=""
              draggable={false}
              className="bp-crop-stage__img"
              style={{
                width: dw,
                height: dh,
                left: (PREVIEW - dw) / 2 + panX,
                top: (PREVIEW - dh) / 2 + panY,
              }}
            />
          ) : (
            <p className="bp-muted">Carregando…</p>
          )}
        </div>

        <label className="bp-field bp-crop-zoom">
          <span>Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            disabled={!ready || busy}
            onChange={(e) => onZoomChange(Number(e.target.value))}
          />
        </label>

        {error ? <p className="bp-error">{error}</p> : null}

        <div className="bp-crop-actions">
          <button type="button" className="bp-btn" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="bp-btn bp-btn--primary"
            onClick={() => void confirm()}
            disabled={!ready || busy}
          >
            {busy ? 'Salvando…' : 'Usar foto'}
          </button>
        </div>
      </div>
    </div>
  )
}
